/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconImg from "@/assets/icones/images.svg?react";
import IconOds from "@/assets/icones/ods.svg?react";
import IconPdf from "@/assets/icones/pdf.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { copyDocumentLink } from "@/documents/utils/copyDocumentLink";
import { downloadDocumentFile } from "@/documents/utils/downloadDocumentFile";
import {
  showDownloadErrorToast,
  showDownloadInProgressToast,
  showDownloadSuccessToast,
} from "@/documents/utils/downloadToast";
import { Download, Eye, Info, Link, MoreVertical, Trash2 } from "lucide-react";
import { useState } from "react";
import DocumentDetailsDialog from "./DocumentDetailsDialog";

type OfficeThumbnailProps = {
  srcUrl: string;
  downloadUrl?: string;
  width?: string;
  height?: string;
  extension: string | undefined;
  treeFinancer: any;
  pj: any;
  otherpage?: boolean;
  canDelete?: boolean;
  onDelete?: (id: number) => void | Promise<void>;
  onShowDetails?: (doc: any) => void;
};

export const OfficeThumbnail = ({
  srcUrl,
  downloadUrl,
  width = "248px",
  height = "320px",
  extension,
  treeFinancer,
  pj,
  otherpage = false,
  canDelete = false,
  onDelete,
  onShowDetails,
}: OfficeThumbnailProps) => {
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const encoded = encodeURIComponent(srcUrl);
  const iframeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;
  const handleCopyLink = async () => {
    try {
      await copyDocumentLink(srcUrl);
      toast({ title: "Lien copié" });
    } catch {
      toast({ title: "Impossible de copier le lien", variant: "destructive" });
    }
  };
  const handleDownload = async () => {
    const downloadToast = showDownloadInProgressToast({
      filename: pj.name,
      kind: "file",
    });

    try {
      await downloadDocumentFile(downloadUrl ?? srcUrl, pj.name);
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename: pj.name, kind: "file" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename: pj.name, kind: "file" });
    }
  };
  const handleShowDetails = () => {
    if (onShowDetails) {
      onShowDetails(pj);
      return;
    }

    setDetailsOpen(true);
  };
  const icon: Record<string, JSX.Element> = {
    pdf: <IconPdf width={18} height={18} />,
    doc: <IconWord width={18} height={18} />,
    docx: <IconWord width={18} height={18} />,
    xlsx: <IconExcel width={18} height={18} />,
    ods: <IconOds width={18} height={18} />,
  };
  return (
    <div className={`bg-muted  ${!otherpage && "p-3"} rounded-md relative`}>
      <div className="flex justify-between items-center relative z-10 mb-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="flex items-center gap-3 "
                style={{ width: "calc(100% - 45px)" }}
              >
                {(extension && icon[extension]) || (
                  <IconImg width={18} height={18} />
                )}
                <h3 className="truncate flex-1">{pj.name}</h3>
              </div>
            </TooltipTrigger>
            <TooltipContent className="flex flex-col gap-2">
              <div>
                Porteur de projet :{" "}
                <strong>
                  {treeFinancer?.find((t) => t.id === pj.pp)?.name}
                </strong>
              </div>
              <div>
                Programme :{" "}
                <strong>
                  {
                    treeFinancer
                      ?.find((t) => t.id === pj.pp)
                      ?.programs.find((p) => p.id === pj.program)?.name
                  }
                </strong>
              </div>
              <div>
                Projet :{" "}
                <strong>
                  {
                    treeFinancer
                      ?.find((t) => t.id === pj.pp)
                      ?.programs.find((p) => p.id === pj.program)
                      ?.projects.find((p) => p.id === pj.project)?.name
                  }
                </strong>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {!otherpage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="px-2 outline-none border-0 border-none ring-0 hover:bg-black/5 w-auto h-auto"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <MoreVertical className="text-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="flex flex-col gap-2" align="end">
              <DropdownMenuItem asChild>
                <button
                  onClick={() => setOpen(true)}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Eye width={22} height={22} /> Ouvrir
                </button>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleShowDetails();
                  }}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Info width={22} height={22} /> Voir les détails
                </button>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDownload();
                  }}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Download width={22} height={22} /> Télécharger
                </button>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyLink();
                  }}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Link width={22} height={22} /> Copier le lien
                </button>
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem asChild>
                  <button
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void onDelete?.(Number(pj.id));
                    }}
                    className="flex gap-2 items-center cursor-pointer text-red-500"
                  >
                    <Trash2 width={22} height={22} /> Supprimer
                  </button>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <div className="relative h-[17rem] overflow-hidden rounded-md bg-white flex items-center justify-center">
            <div className="">
              <div className="cursor-pointer">
                <iframe
                  title="Office Embedded View"
                  src={iframeSrc}
                  width={width}
                  height={height}
                  frameBorder="0"
                  style={{ border: "none", width: width, height: height }}
                  className="pointer-events-none"
                />
              </div>
            </div>
          </div>
        </DialogTrigger>

        <DialogContent className="sm:max-w-4xl sm:w-full h-[90vh] max-w-full flex flex-col">
          <DialogHeader className="max-h-32">
            <DialogTitle>Aperçu complet</DialogTitle>
            <DialogDescription>
              Visualisation via Office Online
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1">
            <iframe
              title="Office Embedded View"
              src={iframeSrc}
              width="100%"
              height="100%"
              style={{ border: "none", flex: 1 }}
            />
          </div>

          <DialogClose asChild>
            <Button variant="outline" className="mt-4 w-max m-auto">
              Fermer
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <DocumentDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        pj={pj}
        treeFinancer={treeFinancer}
      />
    </div>
  );
};
export default OfficeThumbnail;
